import { BaseDrawerProps } from "./Base.drawer"
import { clone, cn, timeout } from "@/lib/utils"
import BaseAction from "./Base.action"
import { useDebouncedCallback } from "@mantine/hooks"
import { useReactFlow, useUpdateNodeInternals } from "@xyflow/react"
import { ScenarioNodeData } from "@/stores/scenario.store"
import { store } from "@/stores/stores"
import FormField from "@/components/form/FormField"
import { Input } from "@/components/ui/input"
import { useForm, UseFormReturn, useWatch } from "react-hook-form"
import Form from "@/components/form/Form"
import { Textarea } from "@/components/ui/textarea"
import { Separator } from "@/components/ui/separator"
import SwitchCollapseInput, { DrawerLabelField } from "@/components/form/SwitchCollapseInput"
import SliderInput from "@/components/form/SliderInput"
import TooltipLabel from "@/components/feedback/TooltipLabel"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useEffect, useMemo } from "react"
import ActionFormBuilder from "@/components/form/FormBuilder/ActionFormBuilder"
import FormBuilder from "@/components/form/FormBuilder"
import { NodeDefinition } from "@/gen"
import { useQueryNodeDynamicProperties } from "@/components/form/FormBuilder/useQueryNodeDynamicProperties"
import { ProgressBarTop } from "@/components/feedback/ProgressBarLoader"
import SelectCredentials from "@/components/form/SelectCredentials"
import { useSnapshot } from "valtio"

interface GenericActionProps extends BaseDrawerProps {
  children?: React.ReactNode
  onClose?: () => void
  title?: React.ReactNode
  action?: ScenarioNodeData
  nodeId: string
}

const onErrorOptions = [
  { value: "stop", label: "Stop", description: "Halt execution and fail the scenario" },
  { value: "continue", label: "Continue", description: "Pass error message as a regular output" },
  // { value: "default_value", label: "Default value", description: "When an error occurs, specify a static output content" },
  { value: "fail_branch", label: "Fail branch", description: "When an error occurs, it will execute the exception branch" },
]

// ██╗  ██╗ ██████╗  ██████╗ ██╗  ██╗███████╗
// ██║  ██║██╔═══██╗██╔═══██╗██║ ██╔╝██╔════╝
// ███████║██║   ██║██║   ██║█████╔╝ ███████╗
// ██╔══██║██║   ██║██║   ██║██╔═██╗ ╚════██║
// ██║  ██║╚██████╔╝╚██████╔╝██║  ██╗███████║
// ╚═╝  ╚═╝ ╚═════╝  ╚═════╝ ╚═╝  ╚═╝╚══════╝
//
const useAutoSave = (props: { form: UseFormReturn<any>; nodeId: string }) => {
  const { updateNodeData } = useReactFlow()
  const updateNodeInternals = useUpdateNodeInternals()

  const values = useWatch({})

  // console.log(values)

  const saveForm = useDebouncedCallback(async (values) => {
    updateNodeData(props.nodeId, values)
    // props.syncForm()

    if (values.on_error) {
      setTimeout(() => {
        updateNodeInternals(props.nodeId)
      }, 50)
    }
  }, 100)

  useEffect(() => {
    saveForm(values)
  }, [saveForm, values])
}

const useNodeActions = (props: { nodeDef: NodeDefinition; form: UseFormReturn<any> }) => {
  const meta_properties = useWatch({ name: "meta_properties" })
  const action = useWatch({ name: "action" })
  const [actionId, operationId] = action?.split?.("::") || []

  // const allActions =
  //   props.nodeDef.actions
  //     ?.map((action) => {
  //       return action.operations
  //     })
  //     .flat() || []

  const actionOperationsRecord = props.nodeDef?.actions?.reduce?.((acc, action) => {
    return {
      ...acc,
      ...action.operations.reduce((acc, operation) => {
        return {
          ...acc,
          [`${action.id}::${operation.id}`]: operation,
        }
      }, {}),
    }
  }, {})

  const selectedOperation = actionOperationsRecord?.[action]

  // const actionProperties = useMemo(() => {
  //   const all = selectedAction?.properties || []
  //   const optional = all.filter((property) => property.optional && !meta_properties?.[property.id]?.visible)
  //   const visible = all.filter((property) => !property.optional || !!meta_properties?.[property.id]?.visible)

  //   return {
  //     all,
  //     optional,
  //     visible,
  //   }
  // }, [meta_properties, selectedAction?.properties])

  return {
    selectedAction: props.nodeDef?.actions?.find((action) => action.id === actionId),
    actionOperationsRecord,
    selectedOperation,
    meta_properties,
  }
}

//  ██████╗ ██████╗ ███╗   ███╗██████╗  ██████╗ ███╗   ██╗███████╗███╗   ██╗████████╗
// ██╔════╝██╔═══██╗████╗ ████║██╔══██╗██╔═══██╗████╗  ██║██╔════╝████╗  ██║╚══██╔══╝
// ██║     ██║   ██║██╔████╔██║██████╔╝██║   ██║██╔██╗ ██║█████╗  ██╔██╗ ██║   ██║
// ██║     ██║   ██║██║╚██╔╝██║██╔═══╝ ██║   ██║██║╚██╗██║██╔══╝  ██║╚██╗██║   ██║
// ╚██████╗╚██████╔╝██║ ╚═╝ ██║██║     ╚██████╔╝██║ ╚████║███████╗██║ ╚████║   ██║
//  ╚═════╝ ╚═════╝ ╚═╝     ╚═╝╚═╝      ╚═════╝ ╚═╝  ╚═══╝╚══════╝╚═╝  ╚═══╝   ╚═╝
//
function GenericActionCore(props: GenericActionProps & { form: UseFormReturn<any> }) {
  const { form, ...rest } = props
  const snap = useSnapshot(store)
  useAutoSave({ form, nodeId: props.nodeId })
  const nodeDef = store.scenario.nodeDefinitions.record[props.action?.node_type!]
  const { actionOperationsRecord, selectedAction, selectedOperation, meta_properties } = useNodeActions({ nodeDef, form })

  // const dynamicPropertiesState = useQueryNodeDynamicProperties({
  //   node_type: nodeDef.type,
  //   form,
  //   enabled: !!nodeDef.dynamic_properties?.enabled,
  //   depends_on: nodeDef.dynamic_properties?.depends_on,
  // })

  const actionDynamicPropertiesState = useQueryNodeDynamicProperties({
    node_type: nodeDef.type,
    action_id: selectedAction?.id,
    operation_id: selectedOperation?.id,
    form,
    enabled: !!selectedOperation?.dynamic_properties?.enabled,
    depends_on: selectedOperation?.dynamic_properties?.depends_on,
  })

  const on_error = useWatch({ name: "on_error" })
  const loading = actionDynamicPropertiesState.isLoading || snap.credential.fetching

  // const properties = useMemo(() => {
  //   return [...(nodeDef.properties || []), ...(dynamicPropertiesState.data?.properties || [])]
  // }, [dynamicPropertiesState.data?.properties, nodeDef.properties])

  const actionOperationProperties = useMemo(() => {
    return [...(selectedOperation?.properties || []), ...(actionDynamicPropertiesState.data?.properties || [])]
  }, [actionDynamicPropertiesState.data?.properties, selectedOperation?.properties])

  const optionsActionOperationProperties = useMemo(() => {
    return [
      ...(selectedOperation?.optional_properties?.properties || []),
      ...(actionDynamicPropertiesState.data?.optional_properties || []),
    ]
  }, [actionDynamicPropertiesState.data?.optional_properties, selectedOperation?.optional_properties?.properties])

  return (
    <BaseAction
      {...rest}
      // action={action}
      title={
        <div className="w-full space-y-2">
          <div className="flex items-center gap-2">
            <div
              className={"fc size-6.5 rounded-full shadow-node"}
              style={{
                backgroundColor: nodeDef.color,
                ...(nodeDef.text_color && { color: nodeDef.text_color }),
                transform: "translate(0, 0)",
              }}
            >
              <div className="fc">
                {nodeDef.image_src && <img src={nodeDef.image_src} className="size-4" />}
                {/* {(nodeDef as any).iconElement ? (
              <nodeDef.iconElement className="size-6" />
            ) : (
              <img src={nodeDef.image_src} className="size-6" />
            )} */}
              </div>
            </div>
            <div className="flex flex-col">
              <Input className="hidden" />
              <FormField name="label">
                <Input
                  placeholder="Add label..."
                  size="sm"
                  className="h-6 border-none bg-transparent pl-1 !text-[16px] font-semibold shadow-none outline-none"
                  onBlur={() => {
                    form.setValue("label", props.getSafeLabel(form.getValues("label"), props.nodeId))
                  }}
                />
              </FormField>
              {/* <span className="ml-1 text-xs text-secondary">#{props.nodeId}</span> */}
            </div>
            {/* <TextInput
          placeholder="Add label..."
          variant="filled"
          className={`
            flex-1

            [&_input]:overflow-hidden [&_input]:text-ellipsis [&_input]:whitespace-nowrap [&_input]:bg-transparent [&_input]:pl-1.5 [&_input]:text-md [&_input]:text-white

            focus:[&_input]:!bg-[rgba(255,255,255,0.2)]

            hover:[&_input]:!bg-[rgba(255,255,255,0.2)]
          `}
          size="sm"
          style={{
            "--mantine-color-placeholder": "rgba(255,255,255,0.5)",
          }}
          {...form.getInputProps("label")}
        /> */}
            {/* <ActionIcon
        color="dark"
        variant={"subtle"}
        size={"lg"}
        type="button"
        onClick={props.onClose}
        className="absolute -top-0 right-0"
      >
        <IconIconamoonSignTimesFill className="size-6" />
      </ActionIcon> */}
            {/* <PanelButton label="Help" tooltipPosition="bottom" size={"lg"} className={`-mt-[1px] [&_path]:stroke-[2]`}>
          <IconMaterialSymbolsHelpOutline className="!size-5 text-white" />
        </PanelButton> */}
          </div>
          <FormField form={form} name="description">
            <Textarea
              placeholder="Add description..."
              className="min-h-6 w-full resize-none border-none bg-transparent pl-1 !text-[13px] font-normal shadow-none focus-visible:ring-transparent"
            />
          </FormField>
        </div>
      }
      contentClassName="p-0"
    >
      {/* Custom */}
      {(!!nodeDef.actions?.length || nodeDef.credentials?.length) && (
        <>
          <Separator className="opacity-50 mb-4" />
          <div className="flex flex-col gap-4 pt-1 pb-2">
            {nodeDef.credentials?.length && (
              <div className="px-5 min-h-[60px]">
                <SelectCredentials form={form} node_type={nodeDef.type} />
                <input type="hidden" {...props.form.register("meta_properties")} />
              </div>
            )}
            {/* {!!properties.length && (
              <div className="px-5">
                <FormBuilder properties={properties} form={form} node_type={nodeDef.type} />
              </div>
            )} */}
            {!!nodeDef.actions?.length && (
              <ActionFormBuilder
                nodeDef={nodeDef}
                nodeType={nodeDef.type}
                actionId={selectedAction?.id}
                operationId={selectedOperation?.id}
                actionOperationsRecord={actionOperationsRecord as any}
                selectedOperation={selectedOperation}
                actionOperationProperties={actionOperationProperties}
                optionalActionOperationProperties={optionsActionOperationProperties}
                meta_properties={meta_properties}
                form={form}
              />
            )}
          </div>
          {/* <DrawerLabelField label={<TooltipLabel tip="">Properties</TooltipLabel>}>
            <DrawerLabelBox>{nodeDef.properties && <FormBuilder properties={nodeDef.properties} form={form} />}</DrawerLabelBox>
          </DrawerLabelField> */}
        </>
      )}

      {/* Defaults */}
      <div className="space-y-5">
        <Separator className="my-4 opacity-50" />
        <SwitchCollapseInput label="Retry on fail" form={form} name="retry_on_fail">
          <div className="flex flex-col gap-2">
            <SliderInput label="Max retries" min={1} max={5} step={1} name="max_retries" />
            <SliderInput label="Retry interval (ms)" min={100} max={5000} step={100} name="retry_interval" />
          </div>
        </SwitchCollapseInput>
        <Separator className="opacity-50" />
        <DrawerLabelField
          label={<TooltipLabel tip="Action to take when the node execution fails">On Error</TooltipLabel>}
          right={
            <FormField form={form} name="on_error">
              <Select>
                <SelectTrigger className="w-46">
                  <SelectValue placeholder="Select action">{onErrorOptions.find((x) => x.value === on_error)?.label}</SelectValue>
                </SelectTrigger>
                <SelectContent className="w-46">
                  {onErrorOptions.map((option) => (
                    <SelectItem value={option.value} key={option.value}>
                      <div className="flex flex-col">
                        <span className={cn("text-sm font-medium")}>{option.label}</span>
                        <span className="text-[11px] text-fg-tertiary">{option.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
          }
        />
      </div>
      {loading && <ProgressBarTop className="top-[108px]" />}
    </BaseAction>
  )
}

function GenericAction(props: GenericActionProps) {
  const { action, ...rest } = props

  // const form = useFormMantine<ScenarioNodeData>({
  //   initialValues: clone(props.action),
  //   // schema: nodeDef.schema! as any,
  //   mode: "controlled",
  //   validateInputOnChange: true,
  //   onValuesChange: (...args) => {
  //     startTransition(() => saveForm(...args))
  //   },
  //   validate: { properties: getBasicPropertiesValidation(nodeDef.properties || []) },
  // })
  const form = useForm({
    // resolver: zodResolver(zLogin),
    defaultValues: { label: "", ...clone(props.action) },
  })

  // useEffect(() => {
  //   setTimeout(() => {
  //     form.validate()
  //   }, 100)
  // }, [])

  return (
    <Form
      form={form}
      onSubmit={(model) => {
        return timeout(50)
      }}
      className="size-full"
    >
      <GenericActionCore {...props} form={form} />
    </Form>
  )
}

export default GenericAction
